import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, RefreshCw, ArrowRight, X, Loader2, AlertTriangle, ChevronRight, ChevronLeft, ExternalLink, Linkedin, Search, Database } from 'lucide-react';
import AuditTriagePanel from './AuditTriagePanel';

const COLL_TO_ROUTE = {
  biotech_companies: '/biotech',
  company_profiles: '/pharma',
  investors: '/investors',
};
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = '/api';

const ISSUE_LABEL = {
  exclusion_fail: 'Service-provider noise',
  biotech_filter_fail: 'Biotech without owned asset',
  pharma_filter_fail: 'Pharma not a BD target',
  ndds_filter_fail: 'NDD without proprietary tech',
  dtx_filter_fail: 'DTx without clinical validation',
  academic_filter_fail: 'Academic without licensable IP',
};

const ISSUE_COLOR = {
  exclusion_fail: 'bg-rose-50 text-rose-700 border-rose-200',
  biotech_filter_fail: 'bg-amber-50 text-amber-800 border-amber-200',
  pharma_filter_fail: 'bg-sky-50 text-sky-700 border-sky-200',
  ndds_filter_fail: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  dtx_filter_fail: 'bg-cyan-50 text-cyan-800 border-cyan-200',
  academic_filter_fail: 'bg-violet-50 text-violet-700 border-violet-200',
};

const COLLECTION_LABEL = {
  biotech_companies: 'Biotech',
  company_profiles: 'Pharma',
  investors: 'Investor',
};

export default function AuditPage() {
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [issueFilter, setIssueFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const PAGE = 50;

  const loadSummary = async () => {
    try {
      const r = await axios.get(`${API}/audit/summary`, { withCredentials: true });
      setSummary(r.data);
    } catch (e) { /* */ }
  };

  const loadFindings = async (resetSkip = false) => {
    setLoading(true);
    const s = resetSkip ? 0 : skip;
    try {
      const r = await axios.get(`${API}/audit/findings`, {
        withCredentials: true,
        params: { issue_type: issueFilter, collection: collectionFilter, resolved: showResolved ? 1 : 0, skip: s, limit: PAGE },
      });
      setFindings(r.data.items || []);
      setTotal(r.data.total || 0);
      if (resetSkip) setSkip(0);
    } catch (e) { /* */ }
    setLoading(false);
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadFindings(true); /* eslint-disable-line */ }, [issueFilter, collectionFilter, showResolved]);
  useEffect(() => { loadFindings(false); /* eslint-disable-line */ }, [skip]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/audit/findings/${id}/action`, { action }, { withCredentials: true });
      setFindings(prev => prev.filter(f => f.finding_id !== id));
      toast.success(action === 'apply' ? 'Suggestion applied · record archived' : 'Dismissed');
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
    setBusyId(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-600" /> CRM Audit
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Read-only sweep of every record in <code className="text-[11px] bg-slate-100 px-1 rounded">biotech_companies</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">company_profiles</code>, and <code className="text-[11px] bg-slate-100 px-1 rounded">investors</code> against the 6 canonical prompts. Flagged records stay in your CRM unless you click "Apply".
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { loadSummary(); loadFindings(true); }}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(ISSUE_LABEL).map(([k, label]) => {
            const s = summary.by_issue?.[k] || { open: 0, resolved: 0 };
            return (
              <button
                key={k}
                onClick={() => setIssueFilter(issueFilter === k ? '' : k)}
                className={`text-left rounded-lg border px-4 py-3 transition-all ${issueFilter === k ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid={`stat-${k}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
                <div className="text-2xl font-bold text-slate-900 mt-0.5">{s.open}</div>
                <div className="text-[10px] text-slate-400">open · {s.resolved} resolved</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Smart Triage panel — auto-classifies findings into actionable tiers */}
      <AuditTriagePanel onApplied={() => { loadSummary(); loadFindings(true); }} />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <span className="text-slate-400">Filter:</span>
        <button onClick={() => setCollectionFilter('')} className={`px-2.5 py-1 rounded-full ${!collectionFilter ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-500'}`}>All collections</button>
        {['biotech_companies', 'company_profiles', 'investors'].map(c => (
          <button key={c} onClick={() => setCollectionFilter(c)} className={`px-2.5 py-1 rounded-full ${collectionFilter === c ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-500'}`}>
            {COLLECTION_LABEL[c]}
          </button>
        ))}
        <span className="text-slate-300">·</span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} className="accent-slate-900" />
          <span className="text-slate-500">Show resolved</span>
        </label>
        <span className="text-slate-300">·</span>
        <span className="text-slate-500">{total} {showResolved ? 'resolved' : 'open'} {issueFilter ? `· ${ISSUE_LABEL[issueFilter]}` : ''}{collectionFilter ? ` · ${COLLECTION_LABEL[collectionFilter]}` : ''}</span>
      </div>

      {/* Findings table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="findings-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 text-left">Record</th>
              <th className="px-4 py-2.5 text-left w-32">Collection</th>
              <th className="px-4 py-2.5 text-left w-44">Issue</th>
              <th className="px-4 py-2.5 text-left">AI reasoning</th>
              <th className="px-4 py-2.5 text-right w-56">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
            )}
            {!loading && findings.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400 text-sm">
                {showResolved ? 'No resolved findings yet.' : '🎉 No open findings — your CRM is clean for this filter.'}
              </td></tr>
            )}
            {!loading && findings.map(f => (
              <tr key={f.finding_id} className="hover:bg-slate-50/50" data-testid={`finding-row-${f.finding_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900">{f.record_name}</div>
                    {COLL_TO_ROUTE[f.collection] && (
                      <a
                        href={`${COLL_TO_ROUTE[f.collection]}/${f.record_id}`}
                        target="_blank" rel="noreferrer"
                        className="text-slate-400 hover:text-emerald-600 transition-colors"
                        title="Open profile in CRM"
                        data-testid={`crm-link-${f.finding_id}`}
                      >
                        <Database className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {f.website && (
                      <a
                        href={f.website.startsWith('http') ? f.website : `https://${f.website}`}
                        target="_blank" rel="noreferrer"
                        className="text-slate-400 hover:text-sky-600 transition-colors"
                        title={f.website}
                        data-testid={`website-link-${f.finding_id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {f.linkedin && (
                      <a
                        href={f.linkedin.startsWith('http') ? f.linkedin : `https://${f.linkedin}`}
                        target="_blank" rel="noreferrer"
                        className="text-slate-400 hover:text-sky-700 transition-colors"
                        title="LinkedIn"
                        data-testid={`linkedin-link-${f.finding_id}`}
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(f.record_name + ' biotech pipeline')}`}
                      target="_blank" rel="noreferrer"
                      className="text-slate-400 hover:text-amber-600 transition-colors"
                      title="Search Google"
                      data-testid={`google-link-${f.finding_id}`}
                    >
                      <Search className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  {f.current_categories?.length > 0 && (
                    <div className="text-[10px] text-slate-400 mt-0.5">{f.current_categories.join(', ')}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{COLLECTION_LABEL[f.collection] || f.collection}</td>
                <td className="px-4 py-3">
                  <Badge className={`text-[10px] border ${ISSUE_COLOR[f.issue_type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {ISSUE_LABEL[f.issue_type] || f.issue_type}
                  </Badge>
                  {f.issue_reason_code && (
                    <div className="text-[9px] text-slate-400 mt-0.5">{f.issue_reason_code.replace(/_/g, ' ')}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 leading-relaxed max-w-md">{f.reason_text}</td>
                <td className="px-4 py-3 text-right">
                  {f.resolved ? (
                    <span className="text-[11px] text-slate-400">{f.resolution === 'apply' ? 'archived' : 'dismissed'}</span>
                  ) : (
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => act(f.finding_id, 'dismiss')} disabled={busyId === f.finding_id} className="text-[11px] h-7" data-testid={`dismiss-${f.finding_id}`}>
                        <X className="w-3 h-3 mr-1" /> Keep as-is
                      </Button>
                      <Button size="sm" onClick={() => act(f.finding_id, 'apply')} disabled={busyId === f.finding_id} className="bg-rose-600 hover:bg-rose-700 text-[11px] h-7" data-testid={`apply-${f.finding_id}`}>
                        {busyId === f.finding_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><AlertTriangle className="w-3 h-3 mr-1" />Archive</>}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && total > PAGE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <span>{skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => setSkip(Math.max(0, skip - PAGE))} disabled={skip === 0} className="h-7"><ChevronLeft className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" onClick={() => setSkip(skip + PAGE)} disabled={skip + PAGE >= total} className="h-7"><ChevronRight className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
